import type { ProblemAddressResponse, VerifyAddressPointRequest, ProblemAddressesApiGetProblemAddressesRequest } from '@/core/generated/api';
import type { HouseCoordinates, ProblemAddressFilters } from './model/types';
import { houseCoordinatesSchema } from './model/schema';
import type { AddressPointFormValues } from './model/schema';
import { formatCoordinateInput } from '@/shared/lib/coordinateInput';

/** Преобразует фильтры очереди в запрос списка */
export const problemAddressFiltersToQuery = (
    filters: ProblemAddressFilters
): ProblemAddressesApiGetProblemAddressesRequest => ({
    verifiedFrom: filters.verifiedFrom,
    verifiedTo: filters.verifiedTo,
    isVerified: filters.verified,
    search: filters.search || undefined,
    city: filters.city || undefined,
    qcGeo: filters.quality === ''
        ? undefined
        : Number(filters.quality),
    skipCount: filters.first,
    takeCount: filters.rows,
    ascending: true,
});

/** Подготавливает поля проверки координат дома */
export const addressPointResponseToFormValues = (
    point: ProblemAddressResponse
): AddressPointFormValues => ({
    latitude: formatCoordinateInput(point.latitude, 2),
    longitude: formatCoordinateInput(point.longitude, 3),
    coordinatePair: '',
});

/** Подготавливает запрос проверки координат дома */
export const addressPointCoordinatesToRequest = (
    coordinates: HouseCoordinates,
    version: string
): VerifyAddressPointRequest => ({
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    version,
});

/** Получает координаты дома из ответа API */
export const getHouseCoordinates = (point: ProblemAddressResponse): HouseCoordinates | null => (
    typeof point.latitude === 'number' && typeof point.longitude === 'number'
        ? {
            latitude: point.latitude,
            longitude: point.longitude
        }
        : null
);

/** Разбирает пару в порядке широта, долгота */
export const parseCoordinatePair = (value: string): HouseCoordinates | null => {
    const dotPair = value.match(/^\s*([+-]?\d+(?:\.\d+)?)\s*,\s*([+-]?\d+(?:\.\d+)?)\s*$/);
    const pair = dotPair ?? value.match(/^\s*([+-]?\d+(?:[.,]\d+)?)\s*[;\s]\s*([+-]?\d+(?:[.,]\d+)?)\s*$/);

    if (!pair) return null;

    const result = houseCoordinatesSchema.safeParse({
        latitude: pair[1],
        longitude: pair[2]
    });

    return result.success
        ? result.data
        : null;
};

/** Создаёт ссылки для проверки дома во внешних картах */
export const getAddressMapLinks = (
    point: ProblemAddressResponse
): { yandex: string; twoGis: string } => {
    const query = [point.city, point.street, point.houseNumber].filter(Boolean).join(', ');

    return {
        yandex: `https://yandex.ru/maps/?${new URLSearchParams({ text: query })}`,
        twoGis: `https://2gis.ru/search/${encodeURIComponent(query)}`,
    };
};

/** Создаёт ссылку на точку с правильным порядком координат карты */
export const getCoordinateMapLink = (coordinates: HouseCoordinates): string => (
    `https://yandex.ru/maps/?${new URLSearchParams({
        ll: `${coordinates.longitude},${coordinates.latitude}`,
        pt: `${coordinates.longitude},${coordinates.latitude},pm2rdm`,
        z: '17',
    })}`
);

/** Вычисляет расстояние между точками в метрах */
export const getCoordinateDistance = (from: HouseCoordinates, to: HouseCoordinates): number => {
    const radians = Math.PI / 180;
    const latitudeDelta = (to.latitude - from.latitude) * radians;
    const longitudeDelta = (to.longitude - from.longitude) * radians;

    const angle = Math.sin(latitudeDelta / 2) ** 2
        + Math.cos(from.latitude * radians) * Math.cos(to.latitude * radians)
        * Math.sin(longitudeDelta / 2) ** 2;

    return Math.round(6_371_000 * 2 * Math.atan2(Math.sqrt(angle), Math.sqrt(1 - angle)));
};
